// eslint-disable-next-line @typescript-eslint/no-var-requires
import { Link, useNavigate, useSearch } from 'react-location';
import { FC, useEffect, useState } from 'react';
import TextInput from '../../components/core/text-input';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { LocationGenerics } from 'router/location';
import { useMutation } from '@tanstack/react-query';
import useApi from 'utils/use-api';
import { setAuth } from 'state/auth';

interface FormValues {
  email: string;
  password: string;
}

interface TwoFAFormValues {
  token: string;
}

const LoginPage: FC = () => {
  useEffect(() => {
    document.title = 'Login';
  }, []);
  const api = useApi();
  const navigate = useNavigate();

  const [requires2FA, setRequires2FA] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const search = useSearch<LocationGenerics>();
  const loginMutation = useMutation({
    mutationFn: (payload: FormValues) => api.post('/auth/user/login', payload).then(res => res.data),
  });

  const verify2FAMutation = useMutation({
    mutationFn: (payload: { email: string; token: string }) => api.post('/auth/2fa/verify', payload).then(res => res.data),
  });

  const loading = loginMutation.isPending || verify2FAMutation.isPending;

  const handleLoginSuccess = (response: any) => {
    const user = response?.user ?? response?.data?.user;
    const tokenData = response?.tokenData ?? response?.data?.tokenData;
    const accessToken = tokenData?.access?.token || tokenData?.token || tokenData?.accessToken;
    const passwordExpired = response?.passwordExpired;
    const passwordExpiresIn = response?.passwordExpiresIn;

    if (user && accessToken) {
      toast(JSON.stringify({ type: 'success', title: 'Login Success', description: `Logged in as ${user.email}` }));
      setAuth({ user, token: accessToken });

      // Show recovery code warning if used
      if (response?.remainingRecoveryCodes !== undefined) {
        toast(
          JSON.stringify({
            type: response.remainingRecoveryCodes === 0 ? 'error' : 'warning',
            title: 'Recovery Code Used',
            description: response.message,
          }),
        );
      }

      if (passwordExpired || user.mustChangePassword) {
        if (passwordExpired) {
          toast(JSON.stringify({ type: 'warning', title: 'Password Expired', description: 'Your password has expired. Please change it now.' }));
        }
        return navigate({
          replace: true,
          to: '/change-password',
          search: { redirect: search.redirect ?? '/' } as any,
        });
      }

      // Check if user needs to set up 2FA (mandatory)
      if (!user.twoFAEnabled) {
        toast(JSON.stringify({ type: 'warning', title: '2FA Required', description: 'Please set up two-factor authentication to continue.' }));
        return navigate({
          replace: true,
          to: '/security',
        });
      }

      if (passwordExpiresIn !== undefined && passwordExpiresIn > 0) {
        toast(
          JSON.stringify({
            type: 'warning',
            title: 'Password Expiring Soon',
            description: `Your password will expire in ${passwordExpiresIn} day${passwordExpiresIn !== 1 ? 's' : ''}. Please change it soon.`,
          }),
        );
      }

      return navigate({
        replace: true,
        to: search.redirect ?? '/',
      });
    }
    toast(JSON.stringify({ type: 'error', title: 'Login Failed', description: 'Something went wrong' }));
  };

  const { handleSubmit, ...form } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/, 'Email must be a valid email')
        .test('allowed-domain', 'Email must be from @wewire.com or @ogateway.co', value => {
          if (!value) return false;
          return value.toLowerCase().endsWith('@wewire.com') || value.toLowerCase().endsWith('@ogateway.co');
        }),
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: async (values: FormValues) => {
      const response = await loginMutation.mutateAsync(values);

      // Check if 2FA is required
      if (response?.requires2FA) {
        setRequires2FA(true);
        setUserEmail(values.email);
        toast(JSON.stringify({ type: 'info', title: '2FA Required', description: 'Please enter your authentication code' }));
        return;
      }

      handleLoginSuccess(response);
    },
  });

  const { handleSubmit: handleSubmit2FA, ...twoFAForm } = useFormik<TwoFAFormValues>({
    initialValues: {
      token: '',
    },
    validationSchema: Yup.object().shape({
      token: Yup.string().required('Verification code is required').min(6, 'Code must be at least 6 characters'),
    }),
    onSubmit: async (values: TwoFAFormValues) => {
      const response = await verify2FAMutation.mutateAsync({
        email: userEmail,
        token: values.token,
      });
      handleLoginSuccess(response);
    },
  });

  const handleBack = () => {
    setRequires2FA(false);
    setUserEmail('');
    twoFAForm.resetForm();
  };

  return (
    <>
      <div className="mx-auto w-full max-w-sm lg:w-96">
        <div>
          <h1 className="text-4xl text-primary-500 font-manrope font-bold">WireGate Desk</h1>
        </div>

        <div className="mt-10">
          {!requires2FA ? (
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <div className="mt-2">
                    <TextInput id="email" label="Email address" type="email" placeholder="e.g. user@mail.com" {...form} />
                  </div>
                </div>

                <div>
                  <div className="mt-2">
                    <TextInput id="password" label="Password" type="password" placeholder="e.g.  **************" {...form} />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-3 w-3 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 font-manrope text-gray-700">
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm leading-6">
                    <Link to={'/forgot-password'} className="font-semibold font-manrope text-primary-500 hover:text-primary-400">
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code from your authenticator app, or use a recovery code.
                </p>
              </div>
              <form onSubmit={handleSubmit2FA} className="space-y-6">
                <div>
                  <div className="mt-2">
                    <TextInput
                      id="token"
                      label="Verification Code"
                      type="text"
                      placeholder="000000 or XXXX-XXXX"
                      {...twoFAForm}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LoginPage;
